/**
 * useMediaEngine Hook
 * 
 * Hook that provides WebMediaEngine (Vidstack) for all platforms.
 */

import { useRef, useCallback, useEffect, useMemo, useState } from "react";
import { Capacitor } from "@capacitor/core";
import type { MediaPlayerInstance } from "@vidstack/react";
import { WebMediaEngine, createWebMediaEngine } from "./WebMediaEngine";
import type {
	IMediaEngine,
	MediaEngineConfig,
	MediaEngineCallbacks,
	MediaSource,
	MediaMetadata,
	PlaybackState,
	PlaybackPosition,
} from "./types";

// ============================================
// Platform Detection
// ============================================

export type MediaEnginePlatform = "web" | "ios" | "android";

export function getMediaEnginePlatform(): MediaEnginePlatform {
	if (!Capacitor.isNativePlatform()) {
		return "web";
	}
	const platform = Capacitor.getPlatform();
	if (platform === "ios") return "ios";
	if (platform === "android") return "android";
	return "web"; // Fallback
}

// ============================================
// Hook Return Type
// ============================================

export interface UseMediaEngineReturn {
	/** The media engine instance */
	engine: IMediaEngine;
	/** Platform being used */
	platform: MediaEnginePlatform;
	/** Whether using native engine (always false - using Vidstack) */
	isNative: boolean;
	/** Current playback state (reactive) */
	playbackState: PlaybackState;
	
	// Convenience methods that delegate to engine
	load: (source: MediaSource, metadata?: MediaMetadata) => Promise<void>;
	play: () => Promise<void>;
	pause: () => Promise<void>;
	seek: (time: number) => Promise<void>;
	stop: () => Promise<void>;
	getPosition: () => PlaybackPosition;
	getState: () => PlaybackState;
	
	// Attach Vidstack player ref
	attachPlayer: (player: MediaPlayerInstance) => void;
	
	// Event handler wrappers for components
	handleCanPlay: () => void;
	handlePlay: () => void;
	handlePause: () => void;
	handleEnded: () => void;
	handleError: (error: unknown) => void;
	handleTimeUpdate: () => void;
}

// ============================================
// Main Hook
// ============================================

export function useMediaEngine(
	config: MediaEngineConfig,
	callbacks?: MediaEngineCallbacks
): UseMediaEngineReturn {
	const platform = useMemo(() => getMediaEnginePlatform(), []);
	const [playbackState, setPlaybackState] = useState<PlaybackState>("idle");
	
	// Create engine on mount - always use WebMediaEngine (Vidstack)
	const engineRef = useRef<IMediaEngine | null>(null);
	
	if (!engineRef.current) {
		engineRef.current = createWebMediaEngine(config);
		// console.log(`[useMediaEngine] Created WebMediaEngine for platform: ${platform}`);
	}
	
	const engine = engineRef.current;
	
	// Wrap callbacks to track state
	const wrappedCallbacks = useMemo<MediaEngineCallbacks>(() => ({
		...callbacks,
		onStateChange: (state): void => {
			setPlaybackState(state);
			callbacks?.onStateChange?.(state);
		},
	}), [callbacks]);
	
	// Update callbacks when they change
	useEffect(() => {
		engine.setCallbacks(wrappedCallbacks);
	}, [engine, wrappedCallbacks]);
	
	// Cleanup on unmount
	useEffect(() => {
		return (): void => {
			engine.destroy();
		};
	}, [engine]);
	
	// ============================================
	// Convenience methods
	// ============================================
	
	const load = useCallback(
		(source: MediaSource, metadata?: MediaMetadata) => engine.load(source, metadata),
		[engine]
	);
	
	const play = useCallback(() => engine.play(), [engine]);
	const pause = useCallback(() => engine.pause(), [engine]);
	const seek = useCallback((time: number) => engine.seek(time), [engine]);
	const stop = useCallback(() => engine.stop(), [engine]);
	const getPosition = useCallback(() => engine.getPosition(), [engine]);
	const getState = useCallback(() => engine.getState(), [engine]);
	
	// ============================================
	// Player attachment
	// ============================================
	
	const attachPlayer = useCallback(
		(player: MediaPlayerInstance) => {
			if (engine instanceof WebMediaEngine) {
				engine.attachPlayer(player);
			}
		},
		[engine]
	);
	
	// ============================================
	// Event handlers for components
	// ============================================
	
	const handleCanPlay = useCallback(() => {
		if (engine instanceof WebMediaEngine) {
			engine.handleCanPlay();
		}
	}, [engine]);
	
	const handlePlay = useCallback(() => {
		if (engine instanceof WebMediaEngine) {
			engine.handlePlay();
		}
	}, [engine]);
	
	const handlePause = useCallback(() => {
		if (engine instanceof WebMediaEngine) {
			engine.handlePause();
		}
	}, [engine]);
	
	const handleEnded = useCallback(() => {
		if (engine instanceof WebMediaEngine) {
			engine.handleEnded();
		}
	}, [engine]);
	
	const handleError = useCallback(
		(error: unknown) => {
			if (engine instanceof WebMediaEngine) {
				engine.handleError(error);
			}
		},
		[engine]
	);
	
	const handleTimeUpdate = useCallback(() => {
		if (engine instanceof WebMediaEngine) {
			engine.handleTimeUpdate();
		}
	}, [engine]);
	
	return {
		engine,
		platform,
		isNative: false, // Always false - using Vidstack
		playbackState,
		load,
		play,
		pause,
		seek,
		stop,
		getPosition,
		getState,
		attachPlayer,
		handleCanPlay,
		handlePlay,
		handlePause,
		handleEnded,
		handleError,
		handleTimeUpdate,
	};
}

// ============================================
// Simpler hook for components that just need
// to wire up a Vidstack player
// ============================================

export interface UseWebMediaEngineOptions {
	config: MediaEngineConfig;
	playerRef: React.RefObject<MediaPlayerInstance>;
	metadata?: MediaMetadata;
	callbacks?: MediaEngineCallbacks;
	isActive: boolean;
}

/**
 * Simplified hook for Vidstack-based components.
 * Automatically attaches/detaches player based on isActive.
 */
export function useWebMediaEngine(options: UseWebMediaEngineOptions): UseMediaEngineReturn {
	const { config, playerRef, metadata, callbacks, isActive } = options;
	
	const result = useMediaEngine(config, callbacks);
	const { engine, attachPlayer, load } = result;
	
	// Attach player when active and player is ready
	useEffect(() => {
		if (isActive && playerRef.current) {
			attachPlayer(playerRef.current);
			
			// Load metadata for media session
			if (metadata && engine.currentSource) {
				engine.updateMetadata(metadata);
			}
		}
		
		return (): void => {
			if (engine instanceof WebMediaEngine) {
				engine.detachPlayer();
			}
		};
	}, [isActive, playerRef, attachPlayer, metadata, engine, load]);
	
	// Update metadata when it changes
	useEffect(() => {
		if (metadata) {
			engine.updateMetadata(metadata);
		}
	}, [metadata, engine]);
	
	return result;
}

// Re-export types for convenience
export type { MediaEngineConfig, MediaEngineCallbacks, MediaSource, MediaMetadata, PlaybackState, PlaybackPosition };
export { createMediaSource, createHlsSource, createLocalSource } from "./types";
